// Multi-tenant brand theming. Ported verbatim from the customer app's
// `features/branding/colors.ts`. In the Next.js app `brandedColors()` was
// rendered into an inline <style> per request; in the SPAs it is applied at
// runtime with `applyBrandColors()`. The super-admin SPA is single-tenant and
// inlines Safe's computed values instead of calling this.

export function HexToHSL(hex: string): { h: number; s: number; l: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

  if (!result) {
    throw new Error('Could not parse Hex Color');
  }

  const rHex = parseInt(result[1], 16);
  const gHex = parseInt(result[2], 16);
  const bHex = parseInt(result[3], 16);

  const r = rHex / 255;
  const g = gHex / 255;
  const b = bHex / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);

  let h = (max + min) / 2;
  let s = h;
  const l = h;

  if (max === min) {
    // Achromatic
    return { h: 0, s: 0, l };
  }

  const d = max - min;
  s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  switch (max) {
    case r:
      h = (g - b) / d + (g < b ? 6 : 0);
      break;
    case g:
      h = (b - r) / d + 2;
      break;
    case b:
      h = (r - g) / d + 4;
      break;
  }
  h /= 6;

  return {
    h: Math.round(360 * h),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

export function brandedColors(color: string): string {
  const primaryHsl = HexToHSL(color);
  const hue = primaryHsl.h;

  const backgroundL = 100;
  const foregroundL = 3.9;
  const edgeContrast = 88;

  const bDir = backgroundL > 50 ? 1 : -1;
  const backgroundSaturation = 20.0;
  const foregroundSaturation = 10;
  return `
  :root {
    --primary:              hsl(${hue} ${primaryHsl.s}% ${primaryHsl.l}%);
    --primary-foreground:   hsl(${hue} ${foregroundSaturation}% ${primaryHsl.l > 50 ? foregroundL : 100 - foregroundL}%);

    /* Faded primary */
    --ring:                 hsl(${hue} ${primaryHsl.s - 10}% ${primaryHsl.l + (primaryHsl.l > 50 ? -10 : 10)}%);

    --background:           hsl(${hue} ${backgroundSaturation}% ${backgroundL}%);
    --foreground:           hsl(${hue} ${foregroundSaturation}% ${foregroundL}%);
    --card:                 hsl(${hue} ${backgroundSaturation}% ${backgroundL}%);
    --card-foreground:      hsl(${hue} ${foregroundSaturation}% ${foregroundL}%);
    --popover:              hsl(${hue} ${backgroundSaturation}% ${backgroundL}%);
    --popover-foreground:   hsl(${hue} ${foregroundSaturation}% ${foregroundL}%);

    /* Fade slightly */
    --secondary:            hsl(${hue} ${backgroundSaturation}% ${backgroundL + bDir * 3.0}%);
    --secondary-foreground: hsl(${hue} ${foregroundSaturation}% ${foregroundL + bDir * 10}%);

    /* Fade a lot and dampen color */
    --muted:                hsl(${hue} ${backgroundSaturation}% ${backgroundL + bDir * -5.0}%);
    --muted-foreground:     hsl(${hue} ${foregroundSaturation * 0.5}% ${foregroundL + bDir * 30}%);

    /* Secondary with primary accent */
    --accent:               hsl(${hue} ${primaryHsl.s * 0.85}% ${primaryHsl.l > 50 ? 20 : 80}%);
    --accent-foreground:    hsl(${hue} ${primaryHsl.s * 0.85}% ${primaryHsl.l > 50 ? 95 : 5}%);

    /* Accent over background */
    --border:               hsl(${hue} ${primaryHsl.s * 0.25}% ${edgeContrast}%);
    --input:                hsl(${hue} ${primaryHsl.s * 0.25}% ${edgeContrast}%);

    --radius: 0.50rem;
    --destructive: hsl(0 84.2% 60.2%);
    --destructive-foreground: hsl(0 0% 98%);

    --sidebar:         hsl(${hue} ${backgroundSaturation}% ${backgroundL}%);
    --sidebar-foreground:         hsl(${hue} ${foregroundSaturation}% ${foregroundL}%);
    --sidebar-primary:            hsl(${hue} ${primaryHsl.s}% ${primaryHsl.l}%);
    --sidebar-primary-foreground: hsl(${hue} ${foregroundSaturation}% ${primaryHsl.l > 50 ? foregroundL : 100 - foregroundL}%);
    --sidebar-accent:             hsl(${hue} ${foregroundSaturation}% ${primaryHsl.l > 50 ? foregroundL : 100 - foregroundL}%);
    --sidebar-accent-foreground:  hsl(${hue} ${primaryHsl.s}% ${primaryHsl.l}%);
    --sidebar-border:             hsl(${hue} ${primaryHsl.s * 0.25}% ${edgeContrast}%);
    --sidebar-ring:               hsl(${hue} ${primaryHsl.s - 10}% ${primaryHsl.l + (primaryHsl.l > 50 ? -10 : 10)}%);
  }

  .dark {
    --background: hsl(20 14.3% 4.1%);
    --foreground: hsl(0 0% 95%);
    --card: hsl(24 9.8% 10%);
    --card-foreground: hsl(0 0% 95%);
    --popover: hsl(0 0% 9%);
    --popover-foreground: hsl(0 0% 95%);
    --primary: hsl(112 67% 49%);
    --primary-foreground: hsl(0 0% 0%);
    --secondary: hsl(240 3.7% 15.9%);
    --secondary-foreground: hsl(0 0% 98%);
    --muted: hsl(0 0% 15%);
    --muted-foreground: hsl(240 5% 64.9%);
    --accent: hsl(12 6.5% 15.1%);
    --accent-foreground: hsl(0 0% 98%);
    --destructive: hsl(0 62.8% 30.6%);
    --destructive-foreground: hsl(0 85.7% 97.3%);
    --border: hsl(240 3.7% 15.9%);
    --input: hsl(240 3.7% 15.9%);
    --ring: hsl(142.4 71.8% 29.2%);

    --sidebar: hsl(240 5.9% 10%);
    --sidebar-foreground: hsl(240 4.8% 95.9%);
    --sidebar-primary: hsl(224.3 76.3% 48%);
    --sidebar-primary-foreground: hsl(0 0% 100%);
    --sidebar-accent: hsl(240 3.7% 15.9%);
    --sidebar-accent-foreground: hsl(240 4.8% 95.9%);
    --sidebar-border: hsl(240 3.7% 15.9%);
    --sidebar-ring: hsl(217.2 91.2% 59.8%);
  }
  `;
}

// Runtime application of a tenant's brand for the SPA. Injects the generated
// CSS into a single <style id="brand-colors"> so it can be replaced when the
// brand changes without leaking style nodes.
const BRAND_STYLE_ID = 'brand-colors';

export function applyBrandColors(primaryColor: string): void {
  if (typeof document === 'undefined') return;
  let css: string;
  try {
    css = brandedColors(primaryColor);
  } catch {
    return; // invalid hex — keep the fallback theme from index.css
  }
  let style = document.getElementById(
    BRAND_STYLE_ID,
  ) as HTMLStyleElement | null;
  if (!style) {
    style = document.createElement('style');
    style.id = BRAND_STYLE_ID;
    document.head.appendChild(style);
  }
  style.textContent = css;
}
